import type { ApiClient } from './api';
import { getTabId } from './callEndReport';

/**
 * Closed set of the places a recording player is mounted. Reporting groups on these, so a
 * new value here is a wire contract change — add one only alongside a real new player, and
 * keep the two lead-view entry points distinct: they are different affordances (a row
 * action vs. an indicator badge) and cannot be told apart after the fact.
 */
export type RecordingPlaybackSurface =
	| 'leadViewRow'
	| 'leadViewIndicator'
	| 'timeline'
	| 'cardView'
	| 'callLogDetails'
	| 'callLogDrawer'
	| 'focusedViewPastCalls'
	| 'focusedViewLeadHistory';

/**
 * The one spelling of this event anywhere in the client. Nothing validates it server-side —
 * a typo would not fail, it would quietly open a second event stream in the warehouse that
 * nobody is querying, so it must never be written inline.
 */
export const CALL_RECORDING_PLAYBACK_EVENT = 'callRecordingPlayback';

const PHONE_CALL_RESOURCE_TYPE = 'phoneCall';

type ReportInput = {
	phoneCallId: string;
	surface: RecordingPlaybackSurface;
	positionSeconds?: number;
	playbackRate?: number;
};

/**
 * Per-player bookkeeping behind the recording-playback event.
 *
 * One event per listen, not per press. `react-h5-audio-player` raises `play` for a
 * mount-autoplay and for every un-pause alike, so reporting each one would turn a rep who
 * pauses to take a note into several listens of the same recording. Everything after the
 * first play of a recording is therefore dropped, and the reporter has to be told when a
 * listen genuinely ends: the draggable window is a singleton that is never unmounted, only
 * closed, so without `reset` a recording opened again reports nothing at all.
 */
export class RecordingPlaybackReporter {
	private readonly apiClient: ApiClient;
	private currentCallId: string | undefined;

	constructor(apiClient: ApiClient) {
		this.apiClient = apiClient;
	}

	/** The player is closed but still mounted; the next play of this call is a new listen. */
	public reset(): void {
		this.currentCallId = undefined;
	}

	public reportPlay(input: ReportInput): void {
		const { phoneCallId, surface, positionSeconds, playbackRate } = input;

		// Analytics joins the event to the call on this alone, so a report without it is a row
		// nothing can be learned from.
		if (!phoneCallId) {
			return;
		}

		if (phoneCallId === this.currentCallId) {
			return;
		}
		this.currentCallId = phoneCallId;

		// Recording length is deliberately absent: the call carries an authoritative duration,
		// and a second copy measured off the player could only ever disagree with it.
		const properties: Record<string, number> = {};
		if (positionSeconds !== undefined) {
			properties.positionSeconds = positionSeconds;
		}
		if (playbackRate !== undefined) {
			properties.playbackRate = playbackRate;
		}

		try {
			void this.apiClient
				.reportClientEvent({
					eventName: CALL_RECORDING_PLAYBACK_EVENT,
					properties,
					resourceId: phoneCallId,
					resourceType: PHONE_CALL_RESOURCE_TYPE,
					surface,
					tabId: getTabId(),
					tabVisibility: typeof document === 'undefined' ? undefined : document.visibilityState,
				})
				.catch(() => {
					/* telemetry is best-effort */
				});
		} catch {
			/* telemetry is best-effort */
		}
	}
}
